import { useState, useEffect, useRef } from "react";
import * as GeoTIFF from "geotiff";
import client from "../api/client";

// 🎨 پالت رنگی نیاز سرمایی (CU): 0 تا 2000 ساعت
const colorScaleCU = [
  { value: 0, color: [255, 255, 255] },
  { value: 100, color: [247, 251, 255] },
  { value: 200, color: [222, 235, 247] },
  { value: 300, color: [198, 219, 239] },
  { value: 400, color: [158, 202, 225] },
  { value: 500, color: [107, 174, 214] },
  { value: 600, color: [66, 146, 198] },
  { value: 700, color: [33, 113, 181] },
  { value: 800, color: [8, 81, 156] },
  { value: 900, color: [8, 48, 107] },
  { value: 1000, color: [224, 243, 219] },
  { value: 1100, color: [199, 233, 192] },
  { value: 1200, color: [161, 217, 155] },
  { value: 1300, color: [116, 196, 118] },
  { value: 1400, color: [65, 171, 93] },
  { value: 1500, color: [35, 139, 69] },
  { value: 1600, color: [0, 109, 44] },
  { value: 1700, color: [0, 68, 27] },
  { value: 1800, color: [0, 37, 14] },
  { value: 2000, color: [0, 0, 0] },
];

// 🎨 پالت رنگی SPI: از -5 تا +5
const colorScaleSPI = [
  { value: -5, color: [140, 26, 26] },
  { value: -4, color: [217, 74, 74] },
  { value: -3, color: [247, 164, 139] },
  { value: -2, color: [255, 230, 204] },
  { value: -1, color: [255, 255, 255] },
  { value: 0, color: [240, 248, 255] },
  { value: 1, color: [224, 239, 255] },
  { value: 2, color: [127, 168, 217] },
  { value: 3, color: [60, 110, 172] },
  { value: 5, color: [31, 59, 115] },
];

export { colorScaleCU, colorScaleSPI };

const getColor = (value, type) => {
  const scale = type === "CU" ? colorScaleCU : colorScaleSPI;

  if (
    isNaN(value) ||
    value === null ||
    value === undefined ||
    value < -1e5 ||
    value > 1e5
  ) {
    return [0, 0, 0, 0];
  }

  if (type === "CU" && value >= 0 && value <= 10) {
    return [255, 255, 255, 255]; // سفید با آلفای کامل (قابل تنظیم)
  }

  const min = scale[0].value;
  const max = scale[scale.length - 1].value;

  // if (value === 0) return [255, 255, 255, 0];

  if (value >= max) {
    const [r, g, b] = scale[scale.length - 1].color;
    const a = r === 255 && g === 255 && b === 255 ? 0 : 255;
    return [r, g, b, a];
  }

  if (value <= min) {
    const [r, g, b] = scale[0].color;
    const a = r === 255 && g === 255 && b === 255 ? 0 : 255;
    return [r, g, b, a];
  }

  for (let i = 0; i < scale.length - 1; i++) {
    const lower = scale[i];
    const upper = scale[i + 1];

    if (value >= lower.value && value <= upper.value) {
      const ratio = (value - lower.value) / (upper.value - lower.value);
      const r = Math.round(
        lower.color[0] + ratio * (upper.color[0] - lower.color[0])
      );
      const g = Math.round(
        lower.color[1] + ratio * (upper.color[1] - lower.color[1])
      );
      const b = Math.round(
        lower.color[2] + ratio * (upper.color[2] - lower.color[2])
      );
      const a = r === 255 && g === 255 && b === 255 ? 0 : 255;
      return [r, g, b, a];
    }
  }

  return [0, 0, 0, 0]; // اگر هیچی نخورد، شفاف
};

const useTiffProcessor = (tiffPath, type = "CU") => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageBounds, setImageBounds] = useState(null);
  const [hoverValue, setHoverValue] = useState(null);
  const [hoverLatLng, setHoverLatLng] = useState(null);
  const imageRef = useRef(null);
  const canvasRef = useRef(null);
  const rasterDataRef = useRef(null);
  const widthRef = useRef(null);
  const heightRef = useRef(null);

  useEffect(() => {
    // Rasters are now served by the authenticated backend rather than as static
    // assets of the SPA, so this goes through the shared client to pick up the
    // bearer token. A bare fetch() would get a 401.
    if (!tiffPath) {
      setImageUrl(null);
      setImageBounds(null);
      return;
    }

    let cancelled = false;

    const processTiff = async () => {
      try {
        const { data: arrayBuffer } = await client.get(tiffPath, {
          responseType: "arraybuffer",
        });
        if (cancelled) return;
        const tiff = await GeoTIFF.fromArrayBuffer(arrayBuffer);
        const image = await tiff.getImage();
        imageRef.current = image;
        const width = image.getWidth();
        const height = image.getHeight();
        widthRef.current = width;
        heightRef.current = height;

        const raster = await image.readRasters();
        rasterDataRef.current = raster[0];

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        const imageData = ctx.createImageData(width, height);

        for (let i = 0; i < width * height; i++) {
          const value = rasterDataRef.current[i];

          const [r, g, b, a] = getColor(value, type);

          imageData.data[i * 4] = r;
          imageData.data[i * 4 + 1] = g;
          imageData.data[i * 4 + 2] = b;
          imageData.data[i * 4 + 3] = a;
        }

        ctx.putImageData(imageData, 0, 0);
        canvasRef.current = canvas;
        if (cancelled) return;
        setImageUrl(canvas.toDataURL("image/png"));

        const bbox = image.getBoundingBox();
        setImageBounds([
          [bbox[1], bbox[0]], // شمال‌غربی
          [bbox[3], bbox[2]], // جنوب‌شرقی
        ]);
      } catch (err) {
        if (cancelled) return;
        // Clear rather than leave the previous raster on screen -- otherwise a
        // failed load silently shows the wrong month's overlay.
        setImageUrl(null);
        setImageBounds(null);
        console.error("⚠️ خطا در خواندن TIFF:", err);
      }
    };

    processTiff();
    return () => { cancelled = true; };
  }, [tiffPath, type]);

  const handleMouseMove = (event, map) => {
    if (!imageRef.current || !rasterDataRef.current) return;

    const image = imageRef.current;
    const tiepoint = image.getTiePoints()[0]; // [{ i, j, k, x, y, z }]
    const pixelScale = image.getFileDirectory().ModelPixelScale; // [scaleX, scaleY, scaleZ]

    const lng = event.latlng.lng;
    const lat = event.latlng.lat;

    const x = Math.floor((lng - tiepoint.x) / pixelScale[0]);
    const y = Math.floor((tiepoint.y - lat) / pixelScale[1]);

    if (x < 0 || x >= widthRef.current || y < 0 || y >= heightRef.current)
      return;

    const index = y * widthRef.current + x;
    const value = rasterDataRef.current[index];
    setHoverValue(value);
    setHoverLatLng(event.latlng);
  };

  return { imageUrl, imageBounds, hoverValue, hoverLatLng, handleMouseMove };
};

export default useTiffProcessor;
